import { useCallback } from 'react';
import { map, partial } from 'lodash';
import type { WatchQueryFetchPolicy } from '@apollo/client';
import { useCursorPagination } from '@frameio/graphql-client';
import type {
  AccountUser,
  AccountUserGroup,
  PageInfo,
} from '@frameio/graphql-client/generated';
import { useGetAccountUserGroupMembersQuery } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { DEFAULT_INITIAL_PAGE_SIZE, EMPTY_ARRAY } from '@frameio/utils';

type GetGroupMemberArgs = {
  accountUserGroupId: AccountUserGroup['id'];
  count?: number;
  fetchData?: boolean;
  fetchPolicyOptions?: {
    fetchPolicy?: WatchQueryFetchPolicy;
    nextFetchPolicy?: WatchQueryFetchPolicy;
  };
};

export const useGetGroupMembers = ({
  accountUserGroupId,
  count = DEFAULT_INITIAL_PAGE_SIZE,
  fetchData = false,
  fetchPolicyOptions = {},
}: GetGroupMemberArgs) => {
  const accountId = useActiveAccountId();
  const { data, error, fetchNext, refetch, loading, previousData, variables } =
    useCursorPagination(
      useGetAccountUserGroupMembersQuery({
        skip: !fetchData || !accountId,
        variables: {
          accountId,
          page: { first: count },
          accountUserGroupId,
        },
        ...fetchPolicyOptions,
      })
    );

  const source = (loading && !data ? previousData : data)?.account
    ?.accountUserGroup;

  const fetchNextPage = useCallback(
    async (pageInfo: PageInfo): Promise<Array<AccountUser['id']>> => {
      const result = await fetchNext({
        count,
        pageInfo,
      });

      return map(result?.data?.account?.accountUserGroup?.members.nodes, 'id');
    },
    [count, fetchNext]
  );

  return {
    error,
    fetchNextPage: partial(
      fetchNextPage,
      data?.account?.accountUserGroup?.members.pageInfo
    ),
    loading,
    members: source?.members.nodes || EMPTY_ARRAY,
    refetch,
    totalCount: source?.members.totalCount,
    variables,
  };
};
